import { withNormalizedStatus } from '~/lib/ai/status';
import { aiApi } from '~/modules/query/ai';
import type {
  CreatePersonaRequest,
  CreatePersonaResponse,
  DeletePersonaResponse,
  DuplicatePersonaResponse,
  GetPersonaResponse,
  ListPersonasResponse,
  UpdatePersonaRequest,
  UpdatePersonaResponse,
} from '~/modules/query/ai/types';

const seg = encodeURIComponent;

/** The service takes the persona status uppercase. */
const toWire = <T extends { status?: string }>(data: T) =>
  data.status ? { ...data, status: data.status.toUpperCase() } : data;

const normalizeOne = <T extends { data: { status: string } }>(response: T): T => ({
  ...response,
  data: withNormalizedStatus(response.data),
});

export const personasApi = aiApi.injectEndpoints({
  endpoints: (builder) => ({
    listPersonas: builder.query<ListPersonasResponse, void>({
      query: () => ({ url: '/manager/personas', method: 'GET' }),
      transformResponse: (response: ListPersonasResponse): ListPersonasResponse => ({
        ...response,
        data: response.data.map(withNormalizedStatus),
      }),
      providesTags: ['AiPersona'],
    }),
    getPersona: builder.query<GetPersonaResponse, string>({
      query: (id) => ({ url: `/manager/personas/${seg(id)}`, method: 'GET' }),
      transformResponse: normalizeOne<GetPersonaResponse>,
      providesTags: (_result, _error, id) => [{ type: 'AiPersona', id }],
    }),
    createPersona: builder.mutation<CreatePersonaResponse, CreatePersonaRequest>({
      query: (data) => ({ url: '/manager/personas', method: 'POST', data: toWire(data) }),
      transformResponse: normalizeOne<CreatePersonaResponse>,
      invalidatesTags: ['AiPersona'],
    }),
    updatePersona: builder.mutation<UpdatePersonaResponse, UpdatePersonaRequest>({
      query: ({ id, ...data }) => ({
        url: `/manager/personas/${seg(id)}`,
        method: 'PATCH',
        data: toWire(data),
      }),
      transformResponse: normalizeOne<UpdatePersonaResponse>,
      invalidatesTags: (_result, _error, arg) => ['AiPersona', { type: 'AiPersona', id: arg.id }],
    }),
    deletePersona: builder.mutation<DeletePersonaResponse, string>({
      query: (id) => ({ url: `/manager/personas/${seg(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => ['AiPersona', { type: 'AiPersona', id }],
    }),
    duplicatePersona: builder.mutation<DuplicatePersonaResponse, string>({
      query: (id) => ({ url: `/manager/personas/${seg(id)}/duplicate`, method: 'POST' }),
      transformResponse: normalizeOne<DuplicatePersonaResponse>,
      invalidatesTags: ['AiPersona'],
    }),
  }),
});

export const {
  useListPersonasQuery,
  useGetPersonaQuery,
  useLazyGetPersonaQuery,
  useCreatePersonaMutation,
  useUpdatePersonaMutation,
  useDeletePersonaMutation,
  useDuplicatePersonaMutation,
} = personasApi;
